import { useMemo, useState } from 'react';
import {
  Type,
  Square,
  Image,
  Minus,
  PenTool,
  MousePointerClick,
  FileCode,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
  ChevronRight,
  Trash2,
  Copy,
  Zap,
  GripVertical,
  Sparkles,
  Folder,
  FolderOpen,
  ArrowUpToLine,
  ArrowDownToLine,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
} from 'lucide-react';
import { useEffect } from 'react';
import { useStudioStore } from '../../stores/studioStore';
import { useMappingStore } from '../../stores/mappingStore';
import { useFontStore } from '../../stores/fontStore';
import { useFontAliasStore } from '../../stores/fontAliasStore';
import { isElementDynamic } from '../../lib/elementDynamic';
import { resolveFontForCss } from '../../lib/fontResolution';
import type { CanvasElement, GroupElement, TextElement, ButtonElement } from '../../types';

const TYPE_ICONS: Record<string, typeof Type> = {
  text: Type,
  shape: Square,
  image: Image,
  button: MousePointerClick,
  line: Minus,
  path: PenTool,
  svg: FileCode,
};

const INDENT_PX = 12;

interface VisibleRow {
  element: CanvasElement;
  depth: number;
  isGroup: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
}

function buildVisibleRows(
  allElements: CanvasElement[],
  layerElements: CanvasElement[],
  expanded: Set<string>
): VisibleRow[] {
  const byId = new Map(allElements.map((e) => [e.id, e] as const));
  // Elements whose id appears in some GroupElement.childIds are non-root.
  const childOf = new Map<string, string>();
  for (const el of allElements) {
    if (el.type === 'group') {
      for (const cid of (el as GroupElement).childIds) {
        childOf.set(cid, el.id);
      }
    }
  }

  const layerIds = new Set(layerElements.map((e) => e.id));
  const roots = layerElements.filter((e) => {
    const parent = childOf.get(e.id);
    // A child only belongs under its parent when the parent is on the same layer.
    return !parent || !layerIds.has(parent);
  });

  // Sort by zIndex desc so highest is on top (matches prior behavior).
  const byZDesc = (a: CanvasElement, b: CanvasElement) => b.zIndex - a.zIndex;
  roots.sort(byZDesc);

  const rows: VisibleRow[] = [];
  const visit = (el: CanvasElement, depth: number) => {
    if (el.type === 'group') {
      const group = el as GroupElement;
      const liveChildren = group.childIds
        .map((cid) => byId.get(cid))
        .filter((c): c is CanvasElement => !!c);
      const isExpanded = expanded.has(el.id);
      rows.push({
        element: el,
        depth,
        isGroup: true,
        isExpanded,
        hasChildren: liveChildren.length > 0,
      });
      if (isExpanded) {
        const sortedChildren = [...liveChildren].sort(byZDesc);
        for (const child of sortedChildren) visit(child, depth + 1);
      }
    } else {
      rows.push({ element: el, depth, isGroup: false, isExpanded: false, hasChildren: false });
    }
  };

  for (const r of roots) visit(r, 0);
  return rows;
}

export default function LayerPanel() {
  const {
    canvasState,
    selectedElementIds,
    setSelectedElements,
    selectElement,
    updateElement,
    updateElements,
    removeElement,
    duplicateElement,
    reorderElement,
    reorderElements,
  } = useStudioStore();

  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);
  // Store only collapsed group ids so new groups default to expanded.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

  const fonts = useFontStore((s) => s.fonts);
  const aliases = useFontAliasStore((s) => s.aliases);
  const ensureAliasesLoaded = useFontAliasStore((s) => s.ensureLoaded);
  useEffect(() => {
    ensureAliasesLoaded().catch(() => {});
  }, [ensureAliasesLoaded]);

  const unresolvedFontIds = useMemo(() => {
    const set = new Set<string>();
    for (const el of canvasState.elements) {
      if (el.type !== 'text' && el.type !== 'button') continue;
      const te = el as TextElement | ButtonElement;
      const css = (te.fontFamily || '').trim();
      if (!css) continue;
      const res = resolveFontForCss(css, fonts, aliases);
      if (res.status === 'missing') set.add(te.id);
    }
    return set;
  }, [canvasState.elements, fonts, aliases]);

  const toggleExpanded = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandedGroups = useMemo(() => {
    const groupIds = new Set<string>();
    for (const el of canvasState.elements) {
      if (el.type === 'group' && !collapsedGroups.has(el.id)) groupIds.add(el.id);
    }
    return groupIds;
  }, [canvasState.elements, collapsedGroups]);

  const overlayElements = canvasState.elements.filter((el) => el.layer === 'OVERLAY_LAYER');
  const backgroundElements = canvasState.elements.filter((el) => el.layer === 'BACKGROUND_LAYER');

  const overlayRows = useMemo(
    () => buildVisibleRows(canvasState.elements, overlayElements, expandedGroups),
    [canvasState.elements, overlayElements, expandedGroups]
  );
  const backgroundRows = useMemo(
    () => buildVisibleRows(canvasState.elements, backgroundElements, expandedGroups),
    [canvasState.elements, backgroundElements, expandedGroups]
  );

  const overlayVisible = overlayElements.some((el) => el.visible);
  const bgVisible = backgroundElements.some((el) => el.visible);

  const toggleGroupVisibility = (layer: 'OVERLAY_LAYER' | 'BACKGROUND_LAYER', currentlyVisible: boolean) => {
    const elements = canvasState.elements.filter((el) => el.layer === layer);
    updateElements(elements.map((el) => ({ id: el.id, changes: { visible: !currentlyVisible } })));
  };

  const allDisplayedRows = [...overlayRows, ...backgroundRows];

  const handleLayerClick = (elementId: string, flatIndex: number, event: React.MouseEvent) => {
    if (event.shiftKey && lastClickedIndex !== null) {
      const start = Math.min(lastClickedIndex, flatIndex);
      const end = Math.max(lastClickedIndex, flatIndex);
      const rangeIds = allDisplayedRows.slice(start, end + 1).map((r) => r.element.id);
      setSelectedElements(rangeIds);
    } else if (event.ctrlKey || event.metaKey) {
      const currentIds = selectedElementIds || [];
      if (currentIds.includes(elementId)) {
        const newIds = currentIds.filter((id) => id !== elementId);
        setSelectedElements(newIds.length > 0 ? newIds : []);
      } else {
        setSelectedElements([...currentIds, elementId]);
      }
      setLastClickedIndex(flatIndex);
    } else {
      selectElement(elementId);
      setLastClickedIndex(flatIndex);
    }
  };

  const multiSelected = selectedElementIds.length > 1;

  return (
    <div className="w-56 flex-shrink-0 bg-[#111] border-r border-white/[0.06] flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2.5 border-b border-white/[0.06]">
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
          Layers
        </h3>
      </div>

      {multiSelected && (
        <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between bg-white/[0.02]">
          <span className="text-[10px] text-white/50">
            {selectedElementIds.length} selected
          </span>
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => reorderElements(selectedElementIds, 'front')}
              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
              title="Bring all to front"
            >
              <ArrowUpToLine size={13} />
            </button>
            <button
              onClick={() => reorderElements(selectedElementIds, 'forward')}
              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
              title="Move forward"
            >
              <ArrowUp size={13} />
            </button>
            <button
              onClick={() => reorderElements(selectedElementIds, 'backward')}
              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
              title="Move backward"
            >
              <ArrowDown size={13} />
            </button>
            <button
              onClick={() => reorderElements(selectedElementIds, 'back')}
              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
              title="Send all to back"
            >
              <ArrowDownToLine size={13} />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto sidebar-scroll">
        <LayerGroup
          label="Overlay"
          sublabel="ADA-compliant HTML/CSS"
          color="cyan"
          count={overlayElements.length}
          groupVisible={overlayVisible}
          onToggleGroupVisibility={() => toggleGroupVisibility('OVERLAY_LAYER', overlayVisible)}
          rows={overlayRows}
          selectedElementIds={selectedElementIds}
          unresolvedFontIds={unresolvedFontIds}
          flatIndexOffset={0}
          onLayerClick={handleLayerClick}
          onUpdate={updateElement}
          onRemove={removeElement}
          onDuplicate={duplicateElement}
          onReorder={reorderElement}
          onToggleExpanded={toggleExpanded}
        />

        <LayerGroup
          label="Background"
          sublabel="Rendered to JPEG"
          color="amber"
          count={backgroundElements.length}
          groupVisible={bgVisible}
          onToggleGroupVisibility={() => toggleGroupVisibility('BACKGROUND_LAYER', bgVisible)}
          rows={backgroundRows}
          selectedElementIds={selectedElementIds}
          unresolvedFontIds={unresolvedFontIds}
          flatIndexOffset={overlayRows.length}
          onLayerClick={handleLayerClick}
          onUpdate={updateElement}
          onRemove={removeElement}
          onDuplicate={duplicateElement}
          onReorder={reorderElement}
          onToggleExpanded={toggleExpanded}
        />

        {canvasState.elements.length === 0 && (
          <div className="px-3 py-6 text-center">
            <p className="text-[11px] text-zinc-600">
              No elements yet. Use the toolbar to add elements.
            </p>
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t border-white/[0.06] flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-cyan-500" />
          <span className="text-[9px] text-zinc-600 uppercase tracking-wider">Overlay</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-[9px] text-zinc-600 uppercase tracking-wider">Background</span>
        </div>
      </div>
    </div>
  );
}

function LayerGroup({
  label,
  sublabel,
  color,
  count,
  groupVisible,
  onToggleGroupVisibility,
  rows,
  selectedElementIds,
  unresolvedFontIds,
  flatIndexOffset,
  onLayerClick,
  onUpdate,
  onRemove,
  onDuplicate,
  onReorder,
  onToggleExpanded,
}: {
  label: string;
  sublabel: string;
  color: 'cyan' | 'amber';
  count: number;
  groupVisible: boolean;
  onToggleGroupVisibility: () => void;
  rows: VisibleRow[];
  selectedElementIds: string[];
  unresolvedFontIds: Set<string>;
  flatIndexOffset: number;
  onLayerClick: (id: string, flatIndex: number, event: React.MouseEvent) => void;
  onUpdate: (id: string, updates: Partial<CanvasElement>) => void;
  onRemove: (id: string) => void;
  onDuplicate: (id: string) => void;
  onReorder: (id: string, direction: 'up' | 'down' | 'top' | 'bottom') => void;
  onToggleExpanded: (id: string) => void;
}) {
  const dotColor = color === 'cyan' ? 'bg-cyan-500' : 'bg-amber-500';
  const countBg = color === 'cyan' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-amber-500/10 text-amber-400';

  return (
    <div className="border-b border-white/[0.04]">
      <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.02]">
        <span className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
            {label}
          </span>
          <span className="text-[9px] text-zinc-600 ml-1.5">
            {sublabel}
          </span>
        </div>
        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${countBg}`}>
          {count}
        </span>
        <button
          onClick={onToggleGroupVisibility}
          className="p-0.5 rounded text-zinc-600 hover:text-zinc-400 transition-colors"
          title={groupVisible ? `Hide all ${label.toLowerCase()} elements` : `Show all ${label.toLowerCase()} elements`}
        >
          {groupVisible ? <Eye size={12} /> : <EyeOff size={12} />}
        </button>
      </div>

      <div className="py-0.5">
        {rows.map((row, idx) => (
          <LayerItem
            key={row.element.id}
            element={row.element}
            depth={row.depth}
            isGroup={row.isGroup}
            isExpanded={row.isExpanded}
            hasChildren={row.hasChildren}
            isSelected={selectedElementIds.includes(row.element.id)}
            isSingleSelected={selectedElementIds.length === 1 && selectedElementIds[0] === row.element.id}
            hasUnresolvedFont={unresolvedFontIds.has(row.element.id)}
            color={color}
            onSelect={(e) => onLayerClick(row.element.id, flatIndexOffset + idx, e)}
            onUpdate={(updates) => onUpdate(row.element.id, updates)}
            onRemove={() => onRemove(row.element.id)}
            onDuplicate={() => onDuplicate(row.element.id)}
            onReorder={(dir) => onReorder(row.element.id, dir)}
            onToggleExpanded={() => onToggleExpanded(row.element.id)}
          />
        ))}
        {rows.length === 0 && (
          <p className="text-[10px] text-zinc-700 px-3 py-2 italic">
            No {label.toLowerCase()} elements
          </p>
        )}
      </div>
    </div>
  );
}

function LayerItem({
  element,
  depth,
  isGroup,
  isExpanded,
  hasChildren,
  isSelected,
  isSingleSelected,
  hasUnresolvedFont,
  color,
  onSelect,
  onUpdate,
  onRemove,
  onDuplicate,
  onReorder,
  onToggleExpanded,
}: {
  element: CanvasElement;
  depth: number;
  isGroup: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  isSelected: boolean;
  isSingleSelected: boolean;
  hasUnresolvedFont: boolean;
  color: 'cyan' | 'amber';
  onSelect: (e: React.MouseEvent) => void;
  onUpdate: (updates: Partial<CanvasElement>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onReorder: (direction: 'up' | 'down' | 'top' | 'bottom') => void;
  onToggleExpanded: () => void;
}) {
  const editAsBrandData = useStudioStore(s => s.editAsBrandData);
  const elementMappings = useMappingStore(s => s.elementMappings[element.id]);
  const isDynamic = isElementDynamic(element, elementMappings);
  const isMergedSvg = element.type === 'svg' && /\(\d+\)$/.test(element.name);
  const Icon = isGroup
    ? (isExpanded ? FolderOpen : Folder)
    : (isMergedSvg ? Sparkles : (TYPE_ICONS[element.type] || Square));
  const selectedBg = color === 'cyan' ? 'bg-cyan-500/10 border-l-2 border-l-cyan-500' : 'bg-amber-500/10 border-l-2 border-l-amber-500';
  const iconColor = color === 'cyan' ? 'text-cyan-400' : 'text-amber-400';
  const hasLiveData = !!editAsBrandData && isDynamic;

  const indentStyle = { paddingLeft: `${8 + depth * INDENT_PX}px` };

  return (
    <div
      className="layer-item"
      title={
        isGroup
          ? 'Group \u2014 select and press Ctrl+Shift+G to ungroup'
          : isMergedSvg ? 'Compound SVG \u2014 right-click to Explode' : undefined
      }
    >
      <button
        onClick={onSelect}
        style={indentStyle}
        className={`
          w-full flex items-center gap-1.5 pr-1 py-1.5 text-left transition-all duration-100
          ${isSelected
            ? `${selectedBg} ${iconColor}`
            : 'text-zinc-400 hover:bg-white/[0.03] hover:text-zinc-300 border-l-2 border-l-transparent'
          }
          ${!element.visible ? 'opacity-40' : ''}
          ${element.locked ? 'opacity-70' : ''}
        `}
      >
        {isGroup && hasChildren ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onToggleExpanded();
              }
            }}
            className="p-0.5 -ml-0.5 rounded text-zinc-500 hover:text-zinc-300 cursor-pointer shrink-0"
            aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
          >
            {isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          </span>
        ) : (
          <GripVertical size={10} className="text-zinc-700 shrink-0 cursor-grab" />
        )}
        <Icon size={12} className={isSelected ? iconColor : (isGroup ? 'text-zinc-400' : 'text-zinc-500')} />
        <span className="text-[11px] truncate flex-1 min-w-0">{element.name}</span>
        {hasUnresolvedFont && (
          <span
            className="inline-flex items-center text-red-400 shrink-0"
            title="Font is not available — open Font Manager → Project Fonts to fix the mapping"
          >
            <AlertTriangle size={10} />
          </span>
        )}
        {hasLiveData && (
          <span className="px-1 py-0.5 text-[8px] font-bold text-cyan-400 bg-cyan-500/10 rounded uppercase tracking-wider shrink-0">
            live
          </span>
        )}
        {isDynamic && (
          <Zap size={9} className="text-emerald-400 shrink-0" />
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUpdate({ visible: !element.visible });
          }}
          className="p-0.5 rounded text-zinc-600 hover:text-zinc-400 transition-colors shrink-0"
        >
          {element.visible ? <Eye size={11} /> : <EyeOff size={11} />}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUpdate({ locked: !element.locked });
          }}
          className="p-0.5 rounded text-zinc-600 hover:text-zinc-400 transition-colors shrink-0"
        >
          {element.locked ? <Lock size={11} /> : <Unlock size={11} />}
        </button>
      </button>

      {isSingleSelected && (
        <div className="flex items-center gap-0.5 px-2 py-1 bg-white/[0.02] border-t border-white/[0.04] animate-fade-in">
          <button
            onClick={() => onReorder('top')}
            className="p-1 rounded text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
            title="Move to Top"
          >
            <ChevronsUp size={12} />
          </button>
          <button
            onClick={() => onReorder('up')}
            className="p-1 rounded text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
            title="Move Up"
          >
            <ChevronUp size={12} />
          </button>
          <button
            onClick={() => onReorder('down')}
            className="p-1 rounded text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
            title="Move Down"
          >
            <ChevronDown size={12} />
          </button>
          <button
            onClick={() => onReorder('bottom')}
            className="p-1 rounded text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
            title="Move to Bottom"
          >
            <ChevronsDown size={12} />
          </button>
          <div className="flex-1" />
          <button
            onClick={onDuplicate}
            className="p-1 rounded text-zinc-600 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
            title="Duplicate"
          >
            <Copy size={12} />
          </button>
          <button
            onClick={onRemove}
            className="p-1 rounded text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
